import { useState, useEffect } from 'react';
import api from '../services/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Separator } from '../components/ui/separator';
import { Tabs, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../components/ui/alert-dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { ShoppingCart, Plus, Minus, X, Search, Package, Briefcase, CheckCircle, AlertCircle, History, Trash2, Calendar } from 'lucide-react';
import { toast } from 'sonner';

const PdvPage = () => {
  const [products, setProducts] = useState([]);
  const [services, setServices] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [activeTab, setActiveTab] = useState('products');
  const [cart, setCart] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [selectedCustomerId, setSelectedCustomerId] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [customerSearchTerm, setCustomerSearchTerm] = useState('');
  const [filteredCustomers, setFilteredCustomers] = useState([]);
  const [historyDialogOpen, setHistoryDialogOpen] = useState(false);
  const [transactions, setTransactions] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [selectedTransaction, setSelectedTransaction] = useState(null);
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [transactionToCancel, setTransactionToCancel] = useState(null);

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    if (customerSearchTerm) {
      const filtered = customers.filter(customer =>
        customer.name.toLowerCase().includes(customerSearchTerm.toLowerCase()) ||
        customer.document.includes(customerSearchTerm) ||
        customer.phone.includes(customerSearchTerm)
      );
      setFilteredCustomers(filtered);
    } else {
      setFilteredCustomers(customers);
    }
  }, [customerSearchTerm, customers]);

  const loadData = async () => {
    try {
      setLoading(true);
      const [productsData, servicesData, customersData] = await Promise.all([
        api.getPdvProducts(),
        api.getPdvServices(),
        api.getPdvCustomers(),
      ]);
      setProducts(productsData.filter(p => (p.availableQuantity ?? p.stock ?? 0) > 0));
      setServices(servicesData);
      setCustomers(customersData);
    } catch (error) {
      toast.error(error.message || 'Erro ao carregar dados');
    } finally {
      setLoading(false);
    }
  };

  const loadTransactions = async () => {
    try {
      setLoadingHistory(true);
      const data = await api.getPdvTransactions();
      setTransactions(data);
    } catch (error) {
      toast.error('Erro ao carregar histórico de transações');
    } finally {
      setLoadingHistory(false);
    }
  };

  const handleOpenHistory = () => {
    setHistoryDialogOpen(true);
    loadTransactions();
  };

  const handleCancelClick = (transactionId) => {
    setTransactionToCancel(transactionId);
    setCancelDialogOpen(true);
  };

  const handleCancelTransaction = async () => {
    if (!transactionToCancel) return;

    try {
      await api.cancelPdvTransaction(transactionToCancel);
      toast.success('Transação cancelada com sucesso');
      loadTransactions();
      loadData(); // Reload products to update stock
      setCancelDialogOpen(false);
      setTransactionToCancel(null);
    } catch (error) {
      toast.error(error.message || 'Erro ao cancelar transação');
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    }).format(date);
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
    }).format(value);
  };

  const currentItems = activeTab === 'products' ? products : services;
  
  const filteredItems = currentItems.filter(item =>
    item.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    item.code?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    item.description?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const addToCart = (item, type) => {
    const itemId = type === 'product' ? item.id : item.id;
    const existingItem = cart.find(ci => ci.id === itemId && ci.type === type);
    
    if (existingItem) {
      const availableQuantity = item.availableQuantity ?? item.stock ?? 0;
      if (type === 'product' && existingItem.quantity >= availableQuantity) {
        toast.error('Estoque insuficiente');
        return;
      }
      setCart(cart.map(ci =>
        ci.id === itemId && ci.type === type
          ? { ...ci, quantity: ci.quantity + 1 }
          : ci
      ));
    } else {
      setCart([...cart, {
        id: itemId,
        type,
        name: item.name,
        price: item.price,
        quantity: 1,
        maxStock: type === 'product' ? availableQuantity : null,
        stockName: item.stockName || null,
      }]);
    }
    toast.success(`${item.name} adicionado`);
  };

  const updateQuantity = (id, type, change) => {
    const item = cart.find(ci => ci.id === id && ci.type === type);
    const newQuantity = item.quantity + change;

    if (newQuantity <= 0) {
      removeFromCart(id, type);
      return;
    }

    if (type === 'product' && newQuantity > item.maxStock) {
      toast.error('Estoque insuficiente');
      return;
    }

    setCart(cart.map(ci =>
      ci.id === id && ci.type === type
        ? { ...ci, quantity: newQuantity }
        : ci
    ));
  };

  const removeFromCart = (id, type) => {
    setCart(cart.filter(ci => !(ci.id === id && ci.type === type)));
    toast.success('Item removido');
  };

  const clearCart = () => {
    setCart([]);
    toast.success('Carrinho limpo');
  };

  const calculateTotal = () => {
    return cart.reduce((sum, item) => sum + (item.price * item.quantity), 0);
  };

  const handleCheckout = () => {
    if (cart.length === 0) {
      toast.error('Carrinho vazio');
      return;
    }

    // Check if there are services in cart
    const hasServices = cart.some(item => item.type === 'service');
    if (hasServices) {
      // Open confirmation dialog with customer selection
      setConfirmDialogOpen(true);
      setSelectedCustomerId('');
      setCustomerSearchTerm('');
      setFilteredCustomers(customers);
    } else {
      // No services, proceed directly
      processCheckout();
    }
  };

  const processCheckout = async () => {
    try {
      setIsProcessing(true);
      const items = cart.map(item => ({
        ...(item.type === 'product' ? { productId: item.id } : { serviceId: item.id }),
        quantity: item.quantity,
      }));

      const payload = { items };
      
      // Add customerId if services are present and customer is selected
      const hasServices = cart.some(item => item.type === 'service');
      if (hasServices && selectedCustomerId) {
        payload.customerId = selectedCustomerId;
      }

      const result = await api.createPdvTransaction(payload);
      toast.success('Venda realizada com sucesso!');
      
      // Show message if invoices were generated
      if (result.generatedInvoices && result.generatedInvoices.length > 0) {
        toast.info(`${result.generatedInvoices.length} fatura(s) gerada(s) automaticamente`);
      }
      
      setCart([]);
      setConfirmDialogOpen(false);
      setSelectedCustomerId('');
      loadData();
    } catch (error) {
      toast.error(error.message || 'Erro ao finalizar venda');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleConfirmCheckout = () => {
    const hasServices = cart.some(item => item.type === 'service');
    if (hasServices && !selectedCustomerId) {
      toast.error('Por favor, selecione um cliente para lançar os serviços');
      return;
    }
    processCheckout();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="h-[calc(100vh-8rem)] flex flex-col" data-testid="pdv-page">
      {/* Header */}
      <div className="mb-4 flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">Ponto de Venda (PDV)</h1>
          <p className="text-muted-foreground mt-1">Sistema de vendas de produtos e serviços</p>
        </div>
        <Button
          variant="outline"
          onClick={handleOpenHistory}
          className="flex items-center gap-2"
          data-testid="history-button"
        >
          <History className="w-4 h-4" />
          Histórico de Pedidos
        </Button>
      </div>

      {/* Split Screen Layout */}
      <div className="flex-1 grid grid-cols-1 lg:grid-cols-2 gap-4 min-h-0">
        {/* Left Panel - Product/Service List */}
        <Card className="flex flex-col min-h-0">
          <CardHeader className="flex-shrink-0 space-y-4 pb-4">
            <div>
              <CardTitle>Catálogo</CardTitle>
              <CardDescription>Selecione os itens para venda</CardDescription>
            </div>
            
            {/* Toggle Produto/Serviço */}
            <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
              <TabsList className="grid w-full grid-cols-2">
                <TabsTrigger value="products" data-testid="tab-products">
                  <Package className="w-4 h-4 mr-2" />
                  Produtos
                </TabsTrigger>
                <TabsTrigger value="services" data-testid="tab-services">
                  <Briefcase className="w-4 h-4 mr-2" />
                  Serviços
                </TabsTrigger>
              </TabsList>
            </Tabs>

            {/* Search */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Buscar..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
                data-testid="search-input"
              />
            </div>
          </CardHeader>

          {/* Scrollable List */}
          <CardContent className="flex-1 overflow-y-auto min-h-0 space-y-2">
            {filteredItems.length === 0 ? (
              <div className="text-center py-12 text-muted-foreground">
                <div className="mb-4">
                  {activeTab === 'products' ? (
                    <Package className="w-12 h-12 mx-auto opacity-50" />
                  ) : (
                    <Briefcase className="w-12 h-12 mx-auto opacity-50" />
                  )}
                </div>
                {searchTerm ? 'Nenhum item encontrado' : `Nenhum ${activeTab === 'products' ? 'produto' : 'serviço'} disponível`}
              </div>
            ) : (
              filteredItems.map((item) => (
                <Card
                  key={item.id}
                  className="hover:shadow-md transition-shadow cursor-pointer"
                  onClick={() => addToCart(item, activeTab === 'products' ? 'product' : 'service')}
                  data-testid={`item-${item.id}`}
                >
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex-1 min-w-0">
                        <h3 className="font-semibold truncate">{item.name}</h3>
                        {item.code && (
                          <p className="text-xs text-muted-foreground font-mono mt-1">
                            [{item.code}]
                          </p>
                        )}
                        {item.description && (
                          <p className="text-sm text-muted-foreground line-clamp-2 mt-1">
                            {item.description}
                          </p>
                        )}
                      </div>
                      <div className="flex flex-col items-end gap-2">
                        <span className="text-lg font-bold text-primary whitespace-nowrap">
                          R$ {item.price.toFixed(2)}
                        </span>
                        {activeTab === 'products' && (
                          <Badge variant="outline" className="whitespace-nowrap">
                            {item.availableQuantity ?? item.stock} un.
                          </Badge>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))
            )}
          </CardContent>
        </Card>

        {/* Right Panel - Cart */}
        <Card className="flex flex-col min-h-0">
          <CardHeader className="flex-shrink-0 pb-4">
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="flex items-center gap-2">
                  <ShoppingCart className="w-5 h-5" />
                  Carrinho ({cart.length})
                </CardTitle>
                <CardDescription>Itens adicionados</CardDescription>
              </div>
              {cart.length > 0 && (
                <Button variant="ghost" size="sm" onClick={clearCart}>
                  Limpar
                </Button>
              )}
            </div>
          </CardHeader>

          {/* Scrollable Cart Items */}
          <CardContent className="flex-1 overflow-y-auto min-h-0 space-y-2">
            {cart.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-full text-center text-muted-foreground">
                <ShoppingCart className="w-16 h-16 mb-4 opacity-20" />
                <p>Carrinho vazio</p>
                <p className="text-sm mt-1">Adicione itens do catálogo</p>
              </div>
            ) : (
              cart.map((item) => (
                <Card key={`${item.type}-${item.id}`} className="bg-accent/50">
                  <CardContent className="p-3">
                    <div className="flex items-center gap-3">
                      <div className="flex-shrink-0">
                        {item.type === 'product' ? (
                          <Package className="w-5 h-5 text-muted-foreground" />
                        ) : (
                          <Briefcase className="w-5 h-5 text-muted-foreground" />
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-medium truncate">{item.name}</p>
                        <p className="text-sm text-muted-foreground">
                          R$ {item.price.toFixed(2)} x {item.quantity}
                        </p>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="flex items-center gap-1 bg-background rounded-md">
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-7 w-7"
                            onClick={() => updateQuantity(item.id, item.type, -1)}
                          >
                            <Minus className="w-3 h-3" />
                          </Button>
                          <span className="w-8 text-center text-sm font-medium">{item.quantity}</span>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-7 w-7"
                            onClick={() => updateQuantity(item.id, item.type, 1)}
                          >
                            <Plus className="w-3 h-3" />
                          </Button>
                        </div>
                        <span className="font-bold text-primary min-w-[70px] text-right">
                          R$ {(item.price * item.quantity).toFixed(2)}
                        </span>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-7 w-7"
                          onClick={() => removeFromCart(item.id, item.type)}
                        >
                          <X className="w-4 h-4 text-destructive" />
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))
            )}
          </CardContent>

          {/* Fixed Footer with Total and Checkout */}
          {cart.length > 0 && (
            <div className="flex-shrink-0 border-t p-4 space-y-4">
              <Separator />
              <div className="flex items-center justify-between text-lg">
                <span className="font-semibold">Total:</span>
                <span className="text-2xl font-bold text-primary">
                  R$ {calculateTotal().toFixed(2)}
                </span>
              </div>
              <Button
                className="w-full h-12 text-lg"
                size="lg"
                onClick={handleCheckout}
                data-testid="checkout-button"
              >
                <CheckCircle className="w-5 h-5 mr-2" />
                Finalizar Venda
              </Button>
            </div>
          )}
        </Card>
      </div>

      {/* Confirmation Dialog */}
      <Dialog open={confirmDialogOpen} onOpenChange={setConfirmDialogOpen}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertCircle className="w-5 h-5 text-yellow-600" />
              Confirmar Lançamento de Serviços
            </DialogTitle>
            <DialogDescription>
              Você está prestes a lançar serviços no PDV. As faturas serão geradas automaticamente conforme a recorrência configurada.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div>
              <Label className="text-sm font-semibold mb-2 block">Serviços no carrinho:</Label>
              <div className="space-y-2">
                {cart
                  .filter(item => item.type === 'service')
                  .map((item, index) => (
                    <div key={index} className="flex items-center justify-between p-2 bg-accent rounded-md">
                      <span className="text-sm font-medium">{item.name}</span>
                      <span className="text-sm text-muted-foreground">Qtd: {item.quantity}</span>
                    </div>
                  ))}
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="customer-select" className="text-sm font-semibold">
                Selecionar Cliente *
              </Label>
              <div className="space-y-2">
                <Input
                  id="customer-search"
                  placeholder="Pesquisar por nome, documento ou telefone..."
                  value={customerSearchTerm}
                  onChange={(e) => setCustomerSearchTerm(e.target.value)}
                />
                <div className="max-h-60 overflow-y-auto border rounded-md">
                  {filteredCustomers.length === 0 ? (
                    <div className="p-4 text-center text-sm text-muted-foreground">
                      {customerSearchTerm ? 'Nenhum cliente encontrado' : 'Nenhum cliente cadastrado'}
                    </div>
                  ) : (
                    filteredCustomers.map((customer) => (
                      <div
                        key={customer.id}
                        onClick={() => {
                          setSelectedCustomerId(customer.id);
                          setCustomerSearchTerm(`${customer.name} - ${customer.document}`);
                        }}
                        className={`p-3 cursor-pointer hover:bg-accent border-b last:border-b-0 ${
                          selectedCustomerId === customer.id ? 'bg-accent' : ''
                        }`}
                      >
                        <div className="font-medium">{customer.name}</div>
                        <div className="text-sm text-muted-foreground">
                          {customer.document} {customer.phone && `- ${customer.phone}`}
                        </div>
                      </div>
                    ))
                  )}
                </div>
                {selectedCustomerId && (
                  <div className="text-xs text-muted-foreground">
                    Cliente selecionado: {filteredCustomers.find(c => c.id === selectedCustomerId)?.name}
                  </div>
                )}
              </div>
              <p className="text-xs text-muted-foreground">
                O cliente é obrigatório para lançar serviços no PDV
              </p>
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setConfirmDialogOpen(false);
                setSelectedCustomerId('');
                setCustomerSearchTerm('');
              }}
              disabled={isProcessing}
            >
              Cancelar
            </Button>
            <Button
              onClick={handleConfirmCheckout}
              disabled={isProcessing || !selectedCustomerId}
            >
              {isProcessing ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Processando...
                </>
              ) : (
                <>
                  <CheckCircle className="w-4 h-4 mr-2" />
                  Confirmar Lançamento
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* History Dialog */}
      <Dialog open={historyDialogOpen} onOpenChange={setHistoryDialogOpen}>
        <DialogContent className="sm:max-w-[800px] max-h-[90vh] flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <History className="w-5 h-5" />
              Histórico de Pedidos
            </DialogTitle>
            <DialogDescription>
              Visualize todas as transações realizadas no PDV
            </DialogDescription>
          </DialogHeader>
          
          <div className="flex-1 overflow-y-auto min-h-0">
            {loadingHistory ? (
              <div className="flex items-center justify-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
              </div>
            ) : transactions.length === 0 ? (
              <div className="text-center py-12 text-muted-foreground">
                <ShoppingCart className="w-12 h-12 mx-auto mb-4 opacity-50" />
                <p>Nenhuma transação encontrada</p>
              </div>
            ) : (
              <div className="space-y-4">
                {transactions.map((transaction) => (
                  <Card
                    key={transaction.id}
                    className={`${
                      transaction.cancelledAt ? 'opacity-60 bg-muted' : ''
                    }`}
                  >
                    <CardHeader className="pb-3">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-2">
                            <CardTitle className="text-lg">
                              Pedido #{transaction.id.slice(0, 8).toUpperCase()}
                            </CardTitle>
                            {transaction.cancelledAt ? (
                              <Badge variant="destructive">Cancelado</Badge>
                            ) : (
                              <Badge variant="default" className="bg-green-600">Ativo</Badge>
                            )}
                          </div>
                          <div className="flex items-center gap-4 text-sm text-muted-foreground">
                            <div className="flex items-center gap-1">
                              <Calendar className="w-4 h-4" />
                              {formatDate(transaction.createdAt)}
                            </div>
                            {transaction.customer && (
                              <div>
                                Cliente: <span className="font-medium">{transaction.customer.name}</span>
                              </div>
                            )}
                          </div>
                        </div>
                        <div className="text-right">
                          <div className="text-2xl font-bold text-primary">
                            {formatCurrency(transaction.total)}
                          </div>
                          {!transaction.cancelledAt && (
                            <Button
                              variant="ghost"
                              size="sm"
                              className="mt-2 text-destructive hover:text-destructive"
                              onClick={() => handleCancelClick(transaction.id)}
                            >
                              <Trash2 className="w-4 h-4 mr-1" />
                              Cancelar
                            </Button>
                          )}
                        </div>
                      </div>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-2">
                        <Label className="text-sm font-semibold">Itens:</Label>
                        {transaction.items && transaction.items.length > 0 ? (
                          <div className="space-y-2">
                            {transaction.items.map((item, index) => (
                              <div
                                key={index}
                                className="flex items-center justify-between p-2 bg-accent rounded-md"
                              >
                                <div className="flex items-center gap-2 flex-1">
                                  {item.productId ? (
                                    <Package className="w-4 h-4 text-muted-foreground" />
                                  ) : (
                                    <Briefcase className="w-4 h-4 text-muted-foreground" />
                                  )}
                                  <div className="flex-1">
                                    <p className="text-sm font-medium">{item.itemName}</p>
                                    <p className="text-xs text-muted-foreground">
                                      {item.quantity}x {formatCurrency(item.unitPrice)}
                                    </p>
                                  </div>
                                </div>
                                <div className="text-sm font-semibold">
                                  {formatCurrency(item.subtotal)}
                                </div>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <p className="text-sm text-muted-foreground">Nenhum item encontrado</p>
                        )}
                      </div>
                      {transaction.cancelledAt && (
                        <div className="mt-3 pt-3 border-t">
                          <p className="text-xs text-muted-foreground">
                            Cancelado em: {formatDate(transaction.cancelledAt)}
                          </p>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setHistoryDialogOpen(false)}>
              Fechar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Cancel Transaction Alert Dialog */}
      <AlertDialog open={cancelDialogOpen} onOpenChange={setCancelDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Cancelar Transação</AlertDialogTitle>
            <AlertDialogDescription>
              Tem certeza que deseja cancelar esta transação? O estoque dos produtos será restaurado automaticamente.
              Esta ação não pode ser desfeita.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Não, manter transação</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleCancelTransaction}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Sim, cancelar transação
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default PdvPage;
